import { gql } from "graphql-tag";
import { sanitizeJsonLdStrings } from "~/utils/jsonld-sanitize";
import { organizationGraphId } from "~/utils/organization-schema";

export const getSeoFields = pageType => gql`
  fragment ${pageType}SEOFields on ${pageType} {
    metaTitle: meta_title
    metaDescription: meta_description
    metaRobots: meta_robots
  }
`;

export const getMetaFields = pageType => gql`
  fragment ${pageType}MetaFields on ${pageType} {
    _linkType
    _meta {
      id
      uid
      type
      tags
    }
  }
`;

export default {
  head() {
    const additionalLinks = this.additionalLinks ?? [];
    const additionalScripts = this.additionalScripts ?? [];

    const base = (this.$config.appEndPoint || "").replace(/\/$/, "") || "";
    const pathOnly = this.$route.path || "/";
    let canonicalHref = `${base}${pathOnly}`;
    try {
      if (this.$prismic && typeof this.$prismic.asLink === "function") {
        canonicalHref = `${base}${this.$prismic.asLink({
          ...this.metaFields,
          fromCanonical: true,
          page: this.$route.query.page,
        })}`;
      }
    } catch {
      canonicalHref = `${base}${pathOnly}`;
    }

    const ldJsonSanitizers = {};
    for (const s of additionalScripts) {
      if (s?.type === "application/ld+json" && s.hid) {
        ldJsonSanitizers[s.hid] = ["innerHTML"];
      }
    }

    // Build WebPage JSON-LD
    const publisherId = organizationGraphId(base);
    const webpageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      ...(this.metaTitle ? { name: this.metaTitle } : {}),
      ...(this.metaDescription ? { description: this.metaDescription } : {}),
      url: canonicalHref,
      publisher: { "@id": publisherId, "@type": "Organization" },
    };

    const webpageScript = {
      hid: "jsonld-webpage",
      type: "application/ld+json",
      json: sanitizeJsonLdStrings(webpageSchema),
    };

    // Separate preload links to place them first for maximum priority
    const preloadLinks = additionalLinks.filter(link => link.rel === "preload");
    const otherLinks = additionalLinks.filter(link => link.rel !== "preload");

    const out = {
      title: this.metaTitle || "",
      meta: [
        {
          hid: "description",
          name: "description",
          content: this.metaDescription || "",
        },
        {
          hid: "robots",
          name: "robots",
          content: this.metaRobots || "follow, index",
        },
      ],
      link: [
        ...preloadLinks,
        {
          rel: "canonical",
          href: canonicalHref,
        },
        ...otherLinks,
      ],
      script: [...additionalScripts, webpageScript],
    };

    if (Object.keys(ldJsonSanitizers).length) {
      out.__dangerouslyDisableSanitizersByTagID = ldJsonSanitizers;
    }

    return out;
  },
};
